import {createSelector} from 'reselect';
import FEATURE_FLAGS from '../../utils/featureFlags';
import {featureFlagsSelector} from '../gsConfig/selectors';

const selectSellTicket = (state) => state && state.sellTicket || {};

const ticketsSelector = (state) => state?.tickets || {};

const sellTicketSelector = createSelector(
  selectSellTicket,
  (sellTicket) => sellTicket
);

const errorSelector = createSelector(
  sellTicketSelector, 
  (sellTicket) => sellTicket.error || null
);

const autoPricingAvailableSelector = createSelector(
  sellTicketSelector,
  (sellTicket) => !!sellTicket?.autoPricingAvailable
);

const pricingSelector = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket.pricing
);

const autoPricingEnabledSelector = createSelector(
  pricingSelector,
  (pricing) => pricing?.autoPricingEnabledInd || null
);

const pricePerProductSelector = createSelector(
  pricingSelector,
  (pricing) => pricing?.pricePerProduct || null
);

const ticketPriceSelector = createSelector(
  pricePerProductSelector, 
  (pricePerProduct) => pricePerProduct?.amount || 0
);

const ticketCurrencySelector = createSelector(
  pricePerProductSelector, 
  (pricePerProduct) => pricePerProduct?.currency || 0
);

const personalDetailsSelector = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket.personalDetails
);

const paymentMethodSelector = createSelector(
  personalDetailsSelector, 
  (personalDetails) => personalDetails?.selectedPayoutMethodTrackingData || null
);

const selectSellerFramework = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket?.sellerFramework || null
);

const selectIsSellerEligible = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket.isSellerEligible || false
);

const selectIsSellerTaxIdVisible = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket.isSellerTaxIdVisible || false
);

const reasonCodeSelector = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket.reasonCode || null
);

const eventMetaDataSelector = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket.eventMetaData || {}
);

const eventMetaDataEventIdSelector = createSelector(
  eventMetaDataSelector,
  (eventMetaData = {}) => {
    const {eventId} = eventMetaData;
    return eventId;
  }
);

const allFulfillmentMethodsSelector = createSelector(
  sellTicketSelector, 
  (sellTicket = {}) => sellTicket.allFulfillmentMethods || []
);

/*
  Fulfillment Methods data selector
*/
const fulfillmentMethodsSelector = createSelector(
  selectSellTicket,
  (state) => state?.events?.events?.[0]?.fulfillmentMethods,
  (sellTicket, fallback) => sellTicket.eventMetaData?.fulfillmentMethods || fallback || null
);

const selectedFulfillmentMethod = (ticketTypeSelected) => {
  return createSelector(
    fulfillmentMethodsSelector,
    (fulfillmentMethods) => fulfillmentMethods?.find((method) => method.ticketMedium === ticketTypeSelected)
  );
};

// TODO: Optimize factory so selectedFulfillmentMethod is only called when ticketTypeSelected changes
const fulfillmentMethodsSelectorFactory = createSelector(
  selectedFulfillmentMethod,
  (fulfillmentObjSelector) => fulfillmentObjSelector || {}
);

const ticketDetailsSelector = createSelector(
  sellTicketSelector, 
  (sellTicket) => sellTicket.ticketDetails || {}
);

const salesTaxSelector = createSelector(
  ticketDetailsSelector,
  ({salesTax = {}}) => salesTax
);

const showOriginalTicketPriceSelector = createSelector(
  salesTaxSelector,
  ({applySalesTax = false}) => applySalesTax
);

const ticketTypeSelector = createSelector(
  ticketDetailsSelector, 
  (ticketDetails) => ticketDetails?.ticketTypeSelected || null
);

const ticketFulfillmentMethodSelector = createSelector(
  ticketDetailsSelector, 
  (ticketDetails) => ticketDetails?.ticketData?.fulfillmentType || null
);

const ticketDeliverNowSelector = createSelector(
  ticketDetailsSelector, 
  (ticketDetails) => !!ticketDetails?.deliverNow || null
);

const faceValueSelector = createSelector(
  ticketDetailsSelector, 
  (ticketDetails) => ticketDetails?.faceValue?.price || null
);

const sellerTypeSelector = createSelector(
  selectSellerFramework, 
  (sellerFramework) => (sellerFramework?.HWUserType && sellerFramework.HWUserType[0]) || null
);

const selectTaxInfo = createSelector(
  sellTicketSelector,
  (sellTicket) => sellTicket?.taxInfo || {}
);

const taxInfoSectionCompleteSelector = createSelector(
  selectTaxInfo,
  (taxInfo) => taxInfo.sectionComplete
);

const ticketTypeSelectedSelector = createSelector(
  ticketDetailsSelector,
  (ticketDetails) => ticketDetails.ticketTypeSelected
);

const numTicketsSelector = createSelector(
  ticketsSelector, 
  (tickets) => tickets?.tickets?.length || 0
);

const isTraitsModalMissingOptionEnabledSelector = createSelector(
  featureFlagsSelector, 
  (features = {}) => !!features[FEATURE_FLAGS.common.seatDetails.missingOption]
);

export {
  sellTicketSelector,
  errorSelector,
  showOriginalTicketPriceSelector,
  selectSellTicket,
  autoPricingEnabledSelector,
  autoPricingAvailableSelector,
  personalDetailsSelector,
  pricingSelector,
  ticketDetailsSelector,
  selectSellerFramework,
  taxInfoSectionCompleteSelector,
  selectIsSellerEligible,
  selectIsSellerTaxIdVisible,
  reasonCodeSelector,
  eventMetaDataSelector,
  eventMetaDataEventIdSelector,
  allFulfillmentMethodsSelector,
  fulfillmentMethodsSelectorFactory,
  selectedFulfillmentMethod,
  fulfillmentMethodsSelector,
  ticketTypeSelector,
  ticketFulfillmentMethodSelector,
  ticketDeliverNowSelector,
  faceValueSelector,
  pricePerProductSelector,
  sellerTypeSelector,
  paymentMethodSelector,
  ticketTypeSelectedSelector,
  numTicketsSelector,
  ticketPriceSelector,
  ticketCurrencySelector,
  isTraitsModalMissingOptionEnabledSelector
};
